<script setup>
import { computed, ref, watch, inject, onMounted, onUnmounted } from "vue";
import { VueTelInput } from "vue3-tel-input";
import "vue3-tel-input/dist/vue3-tel-input.css";
import { useUserStore } from "@/store/user";
import { toastFail, toastSuccess } from "@/utils/common";
import { GUEST_WITHDRAW_EMAIL_SUFFIX } from "@/utils/guestWithdrawGuard";
import { updateProfile } from "@/api/user";
import emitter from "@/utils/bus";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:show", "submit"]);

const userStore = useUserStore();
const $swal = inject("$swal");
const email = ref("");
const phone = ref("");
const actionLoading = ref(false);
const phoneValid = ref(false);
const phoneFormatted = ref("");
const submitAttempted = ref(false);
const innerVisible = ref(false);

const resetForm = () => {
  email.value = "";
  phone.value = "";
  submitAttempted.value = false;
  phoneValid.value = false;
  phoneFormatted.value = "";
};

const defaultCountry = computed(() => {
  const region = String(userStore.region || "").trim();
  if (!region) return "";
  const parts = region.split(/[-_]/);
  const code = parts[parts.length - 1];
  if (/^[A-Za-z]{2}$/.test(code)) return code.toUpperCase();
  return "";
});

const telInputKey = computed(() => `${defaultCountry.value || "auto"}-${props.show ? "show" : "hide"}`);

const getEmailError = () => {
  const emailValue = email.value.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailValue) return t("message.email_error_1");
  if (!emailRegex.test(emailValue)) return t("message.email_error_2");
  if (emailValue.endsWith(GUEST_WITHDRAW_EMAIL_SUFFIX)) return t("message.email_error_3");
  return "";
};

const getPhoneError = () => {
  if (!phone.value) return t("message.phone_error_1");
  if (!phoneValid.value) return t("message.phone_error_2");
  return "";
};

const emailError = computed(() => (submitAttempted.value ? getEmailError() : ""));
const phoneError = computed(() => (submitAttempted.value ? getPhoneError() : ""));

const isVisible = computed({
  get: () => props.show || innerVisible.value,
  set: (val) => {
    innerVisible.value = val;
    emit("update:show", val);
  }
});

watch(
  () => props.show,
  (val) => {
    if (val) {
      email.value = "";
      phone.value = userStore.mobile || "";
      submitAttempted.value = false;
      phoneValid.value = false;
      phoneFormatted.value = "";
    }
  }
);

const handleClose = () => {
  resetForm();
  isVisible.value = false;
};

const handleSubmit = () => {
  if (actionLoading.value) return;
  submitAttempted.value = true;
  const emailValue = email.value.trim();
  const emailErrorMsg = getEmailError();
  const phoneErrorMsg = getPhoneError();
  if (emailErrorMsg) {
    toastFail($swal, emailErrorMsg);
    return;
  }
  if (phoneErrorMsg) {
    toastFail($swal, phoneErrorMsg);
    return;
  }
  actionLoading.value = true;
  updateProfile({
    email: emailValue,
    mobile: phoneFormatted.value || phone.value
  }).then((res) => {
    if (res && res.code === 200) {
      userStore.setDATA({
        email: emailValue,
        mobile: phoneFormatted.value || phone.value
      });
      emit("submit", { email: emailValue, phone: phoneFormatted.value || phone.value });
      emitter.emit("guestWithdrawInfoSubmitted", { email: emailValue, phone: phoneFormatted.value || phone.value });
      toastSuccess($swal, t("message.update_success"));
      isVisible.value = false;
      submitAttempted.value = false;
      return;
    }
    toastFail($swal, t("message.update_failed"));
  }).catch(() => {
    toastFail($swal, t("message.update_failed"));
  }).finally(() => {
    actionLoading.value = false;
  });
};

const handlePhoneValidate = (payload) => {
  phoneValid.value = Boolean(payload?.valid);
  phoneFormatted.value = payload?.number || payload?.formatted || "";
};

const handlePhoneInput = (value, phoneObject) => {
  // 处理 value 可能是 InputEvent 对象的情况
  if (value && typeof value === 'object' && value.target) {
    // 如果是事件对象，从 target.value 获取值
    phone.value = value.target.value || "";
  } else {
    phone.value = value || "";
  }
  if (phoneObject && submitAttempted.value) {
    phoneValid.value = Boolean(phoneObject.valid);
    phoneFormatted.value = phoneObject.number || phoneObject.formatted || "";
  }
};

const handleShowGuestWithdrawInfo = (payload) => {
  if (payload === false) {
    handleClose();
    return;
  }
  isVisible.value = true;
};

onMounted(() => {
  emitter.on("showGuestWithdrawInfo", handleShowGuestWithdrawInfo);
});

onUnmounted(() => {
  emitter.off("showGuestWithdrawInfo", handleShowGuestWithdrawInfo);
});
</script>

<template>
  <Transition name="modal-fade">
    <div v-if="isVisible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">{{ t("message.update_info") }}</div>
          <button class="close-button" type="button" @click="handleClose">×</button>
        </div>

        <div class="form">
          <label class="field" :class="{ 'is-error': emailError }">
            <span class="label">{{ t("message.email") }}</span>
            <input v-model.trim="email" type="email" :placeholder="t('message.please_input_email')" />
            <span v-if="emailError" class="error-text">{{ emailError }}</span>
          </label>
          <div class="field" :class="{ 'is-error': phoneError }">
            <span class="label">{{ t("message.phone") }}</span>
            <VueTelInput :key="telInputKey" :value="phone" mode="international" :autoFormat="true"
              :autoDefaultCountry="!defaultCountry" :defaultCountry="defaultCountry || undefined"
              :dropdownOptions="{ showDialCodeInList: true, showDialCodeInSelection: true, showFlags: true }"
              :inputOptions="{ placeholder: t('message.please_input_phone'), type: 'tel' }"
              @validate="handlePhoneValidate" @input="handlePhoneInput" />
            <span v-if="phoneError" class="error-text">{{ phoneError }}</span>
          </div>
        </div>

        <div class="actions">
          <button class="button ghost" type="button" @click="handleClose">{{ t("message.cancel") }}</button>
          <button class="button primary" type="button" @click="handleSubmit">{{ t("message.confirm") }}</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  width: 88vw;
  background: #ffffff;
  border-radius: 3vw;
  padding: 5vw;
  box-shadow: 0 2vw 6vw rgba(0, 0, 0, 0.12);
  overflow: visible;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4vw;
}

.modal-title {
  font-size: 4.8vw;
  font-weight: 600;
  color: #1f2937;
}

.close-button {
  border: none;
  background: transparent;
  font-size: 6vw;
  line-height: 1;
  color: #9ca3af;
  cursor: pointer;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 3.5vw;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 1.5vw;
  font-size: 3.6vw;
  color: #374151;
}

.field input {
  height: 11vw;
  border-radius: 2vw;
  border: 1px solid #e5e7eb;
  padding: 0 3vw;
  font-size: 3.6vw;
  outline: none;
}

.field input:focus {
  border-color: var(--themes-color);
}

.field.is-error input {
  border-color: #ef4444;
}

:deep(.field.is-error .vue-tel-input) {
  border-color: #ef4444;
}

.error-text {
  color: #ef4444;
  font-size: 3.2vw;
}

:deep(.vue-tel-input) {
  height: 11vw;
  border-radius: 2vw;
  border: 1px solid #e5e7eb;
  padding: 0 2vw;
  font-size: 3.6vw;
}

:deep(.vue-tel-input:focus-within) {
  border-color: var(--themes-color);
}

:deep(.vti__dropdown-list) {
  z-index: 1101;
}

:deep(.vti__flag) {
  width: 5.2vw;
  height: 3.6vw;
}

.actions {
  display: flex;
  gap: 3vw;
  margin-top: 5vw;
}

.button {
  flex: 1;
  height: 11vw;
  border-radius: 2vw;
  border: none;
  font-size: 3.6vw;
}

.button.ghost {
  background: #f3f4f6;
  color: #374151;
}

.button.primary {
  background: var(--themes-color);
  color: #ffffff;
}
</style>
